import React from 'react';
import {
  TaskCompletionCreateInputObject,
  TaskCompletionCreateMutation,
} from 'graphql-common';
import { FetchResult } from '@apollo/client';
import {
  CompletableTodoTask,
  StoredCompletion,
  SyncProcess,
  SyncStatus,
} from 'components/TaskSyncTracker/types';
import { removeByIdFromIndexedDB } from 'utils/indexedDBUtils';
import { CacheNames } from 'constants/enums';
import _get from 'lodash/get';
import { captureException } from 'utils/captureException';
import { logger } from '@lib/services/logger';

type SyncTaskCompletionsArgs = {
  onTaskCompletionCreateMutation: (
    v: TaskCompletionCreateInputObject,
  ) => Promise<FetchResult<TaskCompletionCreateMutation>>;
  completions: StoredCompletion[];
  setExpectedSyncItems: React.Dispatch<
    React.SetStateAction<CompletableTodoTask[] | StoredCompletion[]>
  >;
  setFailedSyncItems: React.Dispatch<
    React.SetStateAction<CompletableTodoTask[] | StoredCompletion[]>
  >;
  setFetchedSyncItems: React.Dispatch<
    React.SetStateAction<CompletableTodoTask[] | StoredCompletion[]>
  >;
  setSyncStatus: React.Dispatch<React.SetStateAction<SyncStatus>>;
  onProcessStart: (v: SyncProcess) => void;
  onProcessEnd: () => void;
};

export const syncTaskCompletions = async ({
  completions,
  onTaskCompletionCreateMutation,
  setExpectedSyncItems,
  setFailedSyncItems,
  setFetchedSyncItems,
  setSyncStatus,
  onProcessStart,
  onProcessEnd,
}: SyncTaskCompletionsArgs) => {
  const startTime = Date.now();

  try {
    const newFetchedTasks: StoredCompletion[] = [];
    const newFailedTasks: StoredCompletion[] = [];

    logger.info('Starting task completions sync', {
      'sync.type': 'completions',
      'sync.total_count': completions.length,
    });

    if (completions.length) {
      setSyncStatus(SyncStatus.idle);
      onProcessStart(SyncProcess.completions);
    } else {
      onProcessEnd();
    }
    setExpectedSyncItems(completions);

    // eslint-disable-next-line no-restricted-syntax
    for (const completion of completions) {
      const { id, taskId, taskName, answers } = completion;
      const progress = `${newFetchedTasks.length + newFailedTasks.length + 1}/${completions.length}`;

      logger.debug('Syncing completion', {
        'sync.task_id': taskId,
        'sync.task_name': taskName,
        'sync.progress': progress,
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await onTaskCompletionCreateMutation({ taskId, answers });
        const taskCompletionCreate = _get(res, [
          'data',
          'taskCompletionCreate',
        ]);

        if (taskCompletionCreate) {
          // eslint-disable-next-line no-await-in-loop
          await removeByIdFromIndexedDB(CacheNames.taskCompletions, id);
          newFetchedTasks.push(completion);
          setFetchedSyncItems([...newFetchedTasks]);

          logger.debug('Completion synced successfully', {
            'sync.task_id': taskId,
          });
        } else {
          // Mutation returned but no data - likely GraphQL error in response
          newFailedTasks.push(completion);
          setFailedSyncItems([...newFailedTasks]);

          logger.error('Completion sync failed - no data returned', {
            'sync.task_id': taskId,
            'sync.task_name': taskName,
          });
        }
      } catch (error) {
        newFailedTasks.push(completion);
        setFailedSyncItems([...newFailedTasks]);

        logger.error('Completion sync failed', {
          'sync.task_id': taskId,
          'sync.task_name': taskName,
          'sync.error': error instanceof Error ? error.message : String(error),
        });
      }
    }

    // All tasks processed, set status
    const hasErrors = newFailedTasks.length > 0;
    const durationMs = Date.now() - startTime;

    if (hasErrors) {
      logger.warn('Task completions sync completed with errors', {
        'sync.type': 'completions',
        'sync.synced_count': newFetchedTasks.length,
        'sync.failed_count': newFailedTasks.length,
        'sync.failed_task_ids': newFailedTasks.map((t) => t.taskId),
        'sync.duration_ms': durationMs,
      });
    } else {
      logger.info('Task completions sync completed', {
        'sync.type': 'completions',
        'sync.synced_count': newFetchedTasks.length,
        'sync.duration_ms': durationMs,
      });
    }

    setSyncStatus(hasErrors ? SyncStatus.error : SyncStatus.success);
  } catch (error) {
    const durationMs = Date.now() - startTime;

    logger.error('Task completions sync failed catastrophically', {
      'sync.type': 'completions',
      'sync.error': error instanceof Error ? error.message : String(error),
      'sync.duration_ms': durationMs,
    });

    if (typeof error === 'string') {
      captureException(new Error(error));
    } else if (error instanceof Error) {
      captureException(error);
    }
    setSyncStatus(SyncStatus.error);
    throw error;
  }
};
